import Swiper, { Navigation } from 'swiper';

const initializedSections = new WeakMap();
const desktopAutoplayMediaQuery = '(min-width: 1034px)';
const caseStudiesSnapMediaQuery = '(min-width: 641px) and (max-width: 1033px)';
const mobileCaseStudiesMediaQuery = '(max-width: 640px)';
const reducedMotionMediaQuery = '(prefers-reduced-motion: reduce)';
const caseStudiesMediaPreloadRootMargin = '1200px 0px';
const caseStudiesAutoplayDuration = 20;
const caseStudiesDesktopAutoplayDuration = 20;
const caseStudiesFrameDuration = 4;
const caseStudiesMinSlideDuration = 8;
const caseStudiesGalleryMaxFrames = 5;
const caseStudiesDesktopTransitionDuration = 1.35;
const caseStudiesFillDuration = 1;
const caseStudiesDesktopFrameDuration = 2.4;
const caseStudiesDesktopFadeDuration = 0.6;
const caseStudiesDesktopRestRatio = 0.15;
const caseStudiesSnapTriggerViewportRatio = 0.75;
const caseStudiesSnapReturnViewportRatio = 0.5;
const caseStudiesSnapIdleDelay = 900;
const caseStudiesMediaPromises = new WeakMap();
const caseStudiesEntrySnapEvent = 'case-studies:entry-snap';
const caseStudiesEntrySnapCompleteEvent = 'case-studies:entry-snap-complete';
const caseStudiesEntrySnapCancelEvent = 'case-studies:entry-snap-cancel';

const getGsap = () => globalThis.gsap;
const getScrollTrigger = () => globalThis.ScrollTrigger;

const getCaseStudySections = (scope) => {
  if (scope.matches?.('[data-case-studies]')) {
    return [scope];
  }

  return Array.from(scope.querySelectorAll('[data-case-studies]'));
};

const getOrCreateContentStage = (pinEl) => {
  const existingStage = pinEl.querySelector('.case-studies-sec__content-stage');

  if (existingStage) {
    return existingStage;
  }

  const stage = document.createElement('div');
  stage.className = 'case-studies-sec__content-stage';
  pinEl.appendChild(stage);

  return stage;
};

const getOrCreateCaseStudiesRail = (line) => {
  if (!line) {
    return null;
  }

  const existingRail = line.querySelector('.case-studies-sec__line-progress');

  if (existingRail) {
    return existingRail;
  }

  const progressLine = document.createElement('span');
  progressLine.className = 'case-studies-sec__line-progress';
  progressLine.setAttribute('aria-hidden', 'true');
  line.appendChild(progressLine);

  return progressLine;
};

const getCaseStudiesGalleryFrames = (background, frameSelector) =>
  Array.from(background?.querySelectorAll(frameSelector) ?? []).slice(
    0,
    caseStudiesGalleryMaxFrames
  );

const setCaseStudiesGalleryStart = (gsapInstance, galleryFrames) => {
  if (!galleryFrames.length) return;
  gsapInstance.set(galleryFrames, {
    clipPath: 'inset(0 0 0 0%)',
    scale: 1,
    opacity: 0,
    zIndex: 0,
  });
  gsapInstance.set(galleryFrames[0], { opacity: 1, zIndex: 1 });
};

const addCaseStudiesGalleryTransition = (timeline, galleryFrames, fillStart, fillDuration) => {
  if (galleryFrames.length < 2) return;
  const galleryStepDuration = fillDuration / galleryFrames.length;
  const transitionDuration = 0.6;
  galleryFrames.slice(1).forEach((galleryFrame, index) => {
    const position = fillStart + galleryStepDuration * (index + 1) - transitionDuration;
    timeline.set(galleryFrame, { zIndex: index + 2 }, position);
    timeline.to(galleryFrame, {
      opacity: 1,
      duration: transitionDuration,
      ease: 'sine.inOut',
    }, position);
    timeline.set(galleryFrames[index], { opacity: 0 }, position + transitionDuration);
  });
};

const canUseCaseStudiesDesktopScene = () =>
  typeof window !== 'undefined' &&
  window.matchMedia(desktopAutoplayMediaQuery).matches &&
  !window.matchMedia(reducedMotionMediaQuery).matches;

const canUseCaseStudiesSnap = () =>
  typeof window !== 'undefined' &&
  window.matchMedia(caseStudiesSnapMediaQuery).matches &&
  !window.matchMedia(reducedMotionMediaQuery).matches;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const getPinHeight = (pinEl) => pinEl.offsetHeight;
export const getCaseStudyAutoplayDuration = (
  frameCount,
  defaultDuration = caseStudiesDesktopAutoplayDuration
) => {
  if (!Number.isFinite(frameCount) || frameCount <= 0) {
    return defaultDuration;
  }

  return Math.max(caseStudiesMinSlideDuration, frameCount * caseStudiesFrameDuration);
};

const getCaseStudiesSceneDuration = (slideCount) =>
  slideCount * caseStudiesFillDuration +
  Math.max(0, slideCount - 1) * caseStudiesDesktopTransitionDuration;
export const getCaseStudiesLastSlideStartScrollPosition = (sceneTrigger, slideCount) => {
  if (!sceneTrigger || slideCount < 2) {
    return null;
  }

  const sceneDuration = getCaseStudiesSceneDuration(slideCount);
  const lastSlideStart =
    (slideCount - 1) *
    (caseStudiesFillDuration + caseStudiesDesktopTransitionDuration);

  return (
    sceneTrigger.start +
    (lastSlideStart / sceneDuration) * (sceneTrigger.end - sceneTrigger.start)
  );
};
export const getCaseStudiesDesktopState = (progress, slideCount) => {
  const position = clamp(progress, 0, 1) * Math.max(0, slideCount - 1);
  const from = Math.floor(position);
  const to = Math.min(from + 1, Math.max(0, slideCount - 1));
  const fraction = position - from;
  const settledIndex = fraction <= caseStudiesDesktopRestRatio
    ? from
    : fraction >= 1 - caseStudiesDesktopRestRatio ? to : null;

  return {
    from: settledIndex ?? from,
    to: settledIndex ?? to,
    settledIndex,
  };
};

export const shouldSnapCaseStudiesSection = ({
  sectionTop,
  sectionBottom,
  viewportHeight,
  isScrollingDown,
  hasSnapped,
  currentScrollY,
  sceneStart,
  sceneEnd,
  enteredFromBelow = false,
}) => {
  if (hasSnapped) {
    return false;
  }

  if (
    Number.isFinite(sceneStart) &&
    Number.isFinite(sceneEnd) &&
    Number.isFinite(currentScrollY) &&
    (isScrollingDown
      ? currentScrollY >= sceneStart
      : currentScrollY <= sceneEnd && !enteredFromBelow)
  ) {
    return false;
  }

  if (isScrollingDown) {
    return (
      sectionTop > 0 &&
      sectionTop <= viewportHeight * caseStudiesSnapTriggerViewportRatio
    );
  }

  return (
    sectionTop < 0 &&
    typeof sectionBottom === 'number' &&
    sectionBottom >= viewportHeight * caseStudiesSnapReturnViewportRatio
  );
};

const loadCaseStudiesMedia = (image) => {
  if (!image) {
    return Promise.resolve();
  }

  const existingPromise = caseStudiesMediaPromises.get(image);

  if (existingPromise) {
    return existingPromise;
  }

  const source = image.closest('picture')?.querySelector('source[data-srcset]');
  const src = image.dataset.src;

  if (!src) {
    return Promise.resolve();
  }

  const promise = new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }

      settled = true;
      image.dataset.caseStudiesMediaLoaded = 'true';

      if (typeof image.decode === 'function') {
        image.decode().catch(() => undefined).finally(resolve);
        return;
      }

      resolve();
    };

    image.addEventListener('load', finish, { once: true });
    image.addEventListener('error', finish, { once: true });

    if (source?.dataset.srcset) {
      source.srcset = source.dataset.srcset;
    }

    image.src = src;

    if (image.complete) {
      finish();
    }
  });

  caseStudiesMediaPromises.set(image, promise);

  return promise;
};

const preloadCaseStudiesMedia = (section, mediaSelector) =>
  Promise.all(
    Array.from(section.querySelectorAll(mediaSelector)).map(loadCaseStudiesMedia)
  );

const initCaseStudiesMediaPreload = (section, mediaSelector) => {
  if (typeof IntersectionObserver === 'undefined') {
    preloadCaseStudiesMedia(section, mediaSelector);
    return () => undefined;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      observer.disconnect();
      preloadCaseStudiesMedia(section, mediaSelector);
    },
    {
      rootMargin: caseStudiesMediaPreloadRootMargin,
    }
  );

  observer.observe(section);

  return () => observer.disconnect();
};

const createCaseStudiesScrollSnap = (section) => {
  let hasSnapped = false;
  let lastScrollY = window.scrollY;
  let isScrollingDown = false;
  let snapTimeout;
  let snapTween;
  let activeSnapDetail;
  let enteredFromBelow = false;
  let isPointerDown = false;
  let destroyed = false;
  const heldScrollKeys = new Set();
  const scrollKeys = new Set([' ', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End']);

  const getSceneTrigger = () =>
    getScrollTrigger()?.getAll?.().find((trigger) => trigger.trigger === section);

  const cancelActiveSnap = () => {
    if (!snapTween) return;
    snapTween.kill();
    snapTween = undefined;
    section.dispatchEvent(new CustomEvent(caseStudiesEntrySnapCancelEvent, { detail: activeSnapDetail }));
    activeSnapDetail = undefined;
  };

  const syncSnap = () => {
    if (destroyed || isPointerDown || heldScrollKeys.size || snapTween) return;
    const currentScrollY = window.scrollY;
    const rect = section.getBoundingClientRect();
    const sceneTrigger = getSceneTrigger();
    if (!shouldSnapCaseStudiesSection({
      sectionTop: rect.top,
      sectionBottom: rect.bottom,
      viewportHeight: window.innerHeight,
      isScrollingDown,
      hasSnapped,
      currentScrollY,
      sceneStart: sceneTrigger?.start,
      sceneEnd: sceneTrigger?.end,
      enteredFromBelow,
    })) return;
    const slideCount = section.querySelectorAll('.case-studies-sec__slide').length;
    const target = isScrollingDown
      ? Math.round(currentScrollY + rect.top)
      : getCaseStudiesLastSlideStartScrollPosition(sceneTrigger, slideCount);
    const gsapInstance = getGsap();
    if (target === null || !gsapInstance) return;
    hasSnapped = true;
    activeSnapDetail = isScrollingDown ? undefined : { restartSlideIndex: slideCount - 1 };
    section.dispatchEvent(new CustomEvent(caseStudiesEntrySnapEvent, { detail: activeSnapDetail }));
    const proxy = { value: currentScrollY };
    snapTween = gsapInstance.to(proxy, {
      value: target,
      duration: 0.5,
      ease: 'sine.inOut',
      onUpdate: () => window.scrollTo(0, proxy.value),
      onComplete: () => {
        snapTween = undefined;
        enteredFromBelow = false;
        lastScrollY = window.scrollY;
        section.dispatchEvent(new CustomEvent(caseStudiesEntrySnapCompleteEvent, { detail: activeSnapDetail }));
        activeSnapDetail = undefined;
      },
    });
  };

  const scheduleSnap = () => {
    window.clearTimeout(snapTimeout);
    if (!destroyed && !isPointerDown && !heldScrollKeys.size) {
      snapTimeout = window.setTimeout(syncSnap, caseStudiesSnapIdleDelay);
    }
  };

  const onScroll = () => {
    const currentScrollY = window.scrollY;
    const previousScrollY = lastScrollY;
    lastScrollY = currentScrollY;
    if (snapTween || currentScrollY === previousScrollY) return;
    const rect = section.getBoundingClientRect();
    if (rect.top >= window.innerHeight || rect.bottom <= 0) {
      hasSnapped = false;
      enteredFromBelow = false;
    }
    isScrollingDown = currentScrollY > previousScrollY;
    const sceneTrigger = getSceneTrigger();
    if (!isScrollingDown && Number.isFinite(sceneTrigger?.end) && previousScrollY > sceneTrigger.end && currentScrollY <= sceneTrigger.end) {
      enteredFromBelow = true;
    }
    scheduleSnap();
  };

  const onManualInput = (event) => {
    if (event.type === 'wheel' && (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX))) return;
    if (event.type === 'keydown') {
      if (!scrollKeys.has(event.key) || event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      heldScrollKeys.add(event.key);
    }
    if (event.type === 'pointerdown') isPointerDown = true;
    cancelActiveSnap();
    scheduleSnap();
  };

  const releaseManualInput = (event) => {
    if (event.type === 'keyup') {
      heldScrollKeys.delete(event.key);
    } else {
      isPointerDown = false;
      if (event.type === 'blur') heldScrollKeys.clear();
    }
    scheduleSnap();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('wheel', onManualInput, { passive: true });
  window.addEventListener('pointerdown', onManualInput, { passive: true });
  window.addEventListener('keydown', onManualInput);
  window.addEventListener('pointerup', releaseManualInput, { passive: true });
  window.addEventListener('pointercancel', releaseManualInput, { passive: true });
  window.addEventListener('keyup', releaseManualInput);
  window.addEventListener('blur', releaseManualInput);

  return () => {
    destroyed = true;
    cancelActiveSnap();
    window.clearTimeout(snapTimeout);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('wheel', onManualInput);
    window.removeEventListener('pointerdown', onManualInput);
    window.removeEventListener('keydown', onManualInput);
    window.removeEventListener('pointerup', releaseManualInput);
    window.removeEventListener('pointercancel', releaseManualInput);
    window.removeEventListener('keyup', releaseManualInput);
    window.removeEventListener('blur', releaseManualInput);
  };
};

const createMobileCaseStudiesSlider = (section) => {
  const sliderWrapper = section.querySelector('[data-case-studies-mobile]');
  const sliderEl = sliderWrapper?.querySelector('.swiper');
  const slides = Array.from(
    sliderWrapper?.querySelectorAll('.case-studies-sec__mobile-slide') ?? []
  );
  const progressLines = slides.map((slide) =>
    slide.querySelector('.case-studies-sec__mobile-line-progress')
  );
  const galleryFrames = slides.map((slide) =>
    getCaseStudiesGalleryFrames(
      slide.querySelector('.case-studies-sec__mobile-bg'),
      '.case-studies-sec__mobile-bg-frame'
    )
  );
  const allGalleryFrames = galleryFrames.flat();
  const nextEl = sliderWrapper?.querySelector('.swiper-button-next');
  const prevEl = sliderWrapper?.querySelector('.swiper-button-prev');
  const gsapInstance = getGsap();
  const reducedMotion = window.matchMedia(reducedMotionMediaQuery).matches;

  if (
    !sliderWrapper ||
    !sliderEl ||
    sliderEl.swiper ||
    slides.length < 2 ||
    progressLines.some((line) => !line) ||
    galleryFrames.some((frames) => !frames.length)
  ) {
    return () => undefined;
  }

  const pauseReasons = new Set(['viewport']);
  const canAutoplay = Boolean(gsapInstance && !reducedMotion);
  let swiper;
  let progressTimeline;
  let observer;
  let destroyed = false;

  const resetProgress = () => {
    gsapInstance?.set(progressLines, { '--case-studies-mobile-progress': 0 });
  };
  const resetGalleries = () => {
    if (!gsapInstance) {
      return;
    }

    galleryFrames.forEach((frames) => setCaseStudiesGalleryStart(gsapInstance, frames));
  };

  const syncPlayback = () => {
    if (!progressTimeline || destroyed) {
      return;
    }

    if (document.hidden) {
      pauseReasons.add('document-hidden');
    } else {
      pauseReasons.delete('document-hidden');
    }

    if (pauseReasons.size > 0) {
      progressTimeline.pause();
      return;
    }

    progressTimeline.play();
  };

  const setPauseReason = (reason, shouldPause) => {
    if (shouldPause) {
      pauseReasons.add(reason);
    } else {
      pauseReasons.delete(reason);
    }

    syncPlayback();
  };

  const startProgress = () => {
    if (!canAutoplay || !swiper || destroyed) {
      return;
    }

    progressTimeline?.kill();
    resetProgress();
    resetGalleries();
    progressTimeline = gsapInstance.timeline({
      paused: true,
      onComplete: () => {
        if (!destroyed) {
          swiper?.slideNext();
        }
      },
    });
    const slideDuration = getCaseStudyAutoplayDuration(
      galleryFrames[swiper.realIndex]?.length,
      caseStudiesAutoplayDuration
    );
    progressTimeline.to(progressLines[swiper.realIndex], {
      '--case-studies-mobile-progress': 1,
      duration: slideDuration,
      ease: 'none',
    });
    addCaseStudiesGalleryTransition(
      progressTimeline,
      galleryFrames[swiper.realIndex],
      0,
      slideDuration
    );
    syncPlayback();
  };

  const stopProgressForSlideChange = () => {
    progressTimeline?.kill();
    progressTimeline = undefined;
    resetProgress();
    resetGalleries();
  };
  const pauseForFocus = () => setPauseReason('focus', true);
  const resumeAfterFocusLeaves = () => {
    queueMicrotask(() => {
      if (!sliderWrapper.contains(document.activeElement)) {
        setPauseReason('focus', false);
      }
    });
  };
  section.classList.add('is-mobile-slider');
  swiper = new Swiper(sliderEl, {
    modules: [Navigation],
    slidesPerView: 1,
    speed: 450,
    threshold: 10,
    watchOverflow: true,
    rewind: true,
    navigation:
      nextEl && prevEl
        ? {
            nextEl,
            prevEl,
          }
        : undefined,
    on: {
      slideChangeTransitionStart: stopProgressForSlideChange,
      slideChangeTransitionEnd: startProgress,
      touchStart: () => progressTimeline?.pause(),
      touchEnd: () => {
        if (!swiper.animating) {
          startProgress();
        }
      },
    },
  });

  if (canAutoplay) {
    if (typeof IntersectionObserver === 'undefined') {
      setPauseReason('viewport', false);
    } else {
      observer = new IntersectionObserver(
        ([entry]) => {
          setPauseReason('viewport', entry.intersectionRatio < 0.6);
        },
        { threshold: 0.6 }
      );
      observer.observe(section);
    }

    document.addEventListener('visibilitychange', syncPlayback);
    sliderWrapper.addEventListener('focusin', pauseForFocus);
    sliderWrapper.addEventListener('focusout', resumeAfterFocusLeaves);
    startProgress();
  }

  return () => {
    if (destroyed) {
      return;
    }

    destroyed = true;
    observer?.disconnect();
    progressTimeline?.kill();
    document.removeEventListener('visibilitychange', syncPlayback);
    sliderWrapper.removeEventListener('focusin', pauseForFocus);
    sliderWrapper.removeEventListener('focusout', resumeAfterFocusLeaves);
    gsapInstance?.set([...progressLines, ...allGalleryFrames], {
      clearProps: '--case-studies-mobile-progress,transform,clip-path,opacity,zIndex',
    });
    swiper.destroy(true, true);
    section.classList.remove('is-mobile-slider');
  };
};

export const getCaseStudiesDesktopGalleryDuration = (count) =>
  count > 0 ? count * caseStudiesDesktopFrameDuration + (count - 1) * caseStudiesDesktopFadeDuration : 0;

export const createCaseStudiesDesktopGallery = (frames, progressLine, gsapInstance, options = {}) => {
  const images = frames.map((frame) => frame.querySelector('img'));
  const setOpacity = frames.map((frame) => gsapInstance.quickSetter(frame, 'opacity'));
  const setProgress = gsapInstance.quickSetter(progressLine, '--case-studies-progress');
  let playlist = [];
  let preparation;
  let prepared = false;
  let elapsed = 0;
  let renderedIndex = -1;
  let running = false;
  let completed = false;
  let destroyed = false;

  const show = (index) => {
    frames.forEach((frame, frameIndex) => setOpacity[frameIndex](frameIndex === index ? 1 : 0));
    gsapInstance.set(frames, { zIndex: 0 });
    if (frames[index]) gsapInstance.set(frames[index], { zIndex: 1 });
  };
  const pause = () => {
    running = false;
    gsapInstance.ticker.remove(tick);
  };
  const tick = (_, deltaMilliseconds) => {
    if (!running || destroyed || !prepared || !playlist.length || completed) return;
    const duration = getCaseStudiesDesktopGalleryDuration(playlist.length);
    elapsed = Math.min(duration, elapsed + Math.min(deltaMilliseconds / 1000, 0.1));
    const slotDuration = caseStudiesDesktopFrameDuration + caseStudiesDesktopFadeDuration;
    const slot = Math.min(Math.floor((elapsed + 1e-9) / slotDuration), playlist.length - 1);
    const index = playlist[slot];
    if (index !== renderedIndex) {
      show(index);
      renderedIndex = index;
    }
    const fade = elapsed - slot * slotDuration - caseStudiesDesktopFrameDuration;
    if (fade > 0 && slot < playlist.length - 1) {
      const next = playlist[slot + 1];
      gsapInstance.set(frames[next], { zIndex: 2 });
      setOpacity[next](clamp(fade / caseStudiesDesktopFadeDuration, 0, 1));
    }
    setProgress(elapsed / duration);
    if (elapsed >= duration - 1e-9) {
      completed = true;
      setProgress(1);
      pause();
      options.onComplete?.();
    }
  };
  const freezePlaylist = () => {
    if (destroyed) return false;
    playlist = images.flatMap((image, index) => image?.complete && image.naturalWidth > 0 ? [index] : []);
    prepared = true;
    show(playlist[0] ?? 0);
    if (running && playlist.length) gsapInstance.ticker.add(tick);
    return playlist.length > 0;
  };
  const prepare = () => {
    if (prepared) return Promise.resolve(playlist.length > 0);
    if (preparation) return preparation;
    if (images.every((image) => image?.complete && image.naturalWidth > 0)) {
      return Promise.resolve(freezePlaylist());
    }
    preparation = Promise.all(images.map((image) =>
      Promise.resolve().then(() => (options.loadMedia ?? loadCaseStudiesMedia)(image)).catch(() => undefined)
    )).then(freezePlaylist);
    return preparation;
  };
  const reset = () => {
    pause();
    elapsed = 0;
    completed = false;
    renderedIndex = -1;
    show(playlist[0] ?? 0);
    setProgress(0);
  };

  gsapInstance.set(frames, { clipPath: 'inset(0 0 0 0%)', scale: 1 });
  reset();

  return {
    reset,
    pause,
    prepare,
    isComplete: () => completed,
    play: () => {
      if (running || destroyed || completed) return;
      running = true;
      if (prepared) {
        if (playlist.length) gsapInstance.ticker.add(tick);
      } else {
        prepare();
      }
    },
    destroy: () => {
      destroyed = true;
      pause();
    },
  };
};

export const getCaseStudiesDesktopSnapTarget = ({
  scrollY, start, end, slideCount, direction, originIndex, viewportHeight, nextSectionTop,
}) => {
  if (!direction || slideCount < 2 || end <= start) return null;
  if (scrollY < start) {
    return direction > 0 && scrollY >= start - viewportHeight * 0.4
      ? { target: start, duration: 0.9, delay: 400 }
      : null;
  }
  if (scrollY > end) {
    return direction < 0 && scrollY <= end + viewportHeight * 0.6
      ? { target: end, duration: 0.7, delay: 900 }
      : null;
  }

  const step = (end - start) / (slideCount - 1);
  const position = (scrollY - start) / step;
  const state = getCaseStudiesDesktopState((scrollY - start) / (end - start), slideCount);
  let index = state.settledIndex;
  if (index === null) {
    index = direction > 0 ? Math.ceil(position) : Math.floor(position);
  } else if (index === originIndex) {
    index += direction;
  }

  let target;
  if (index < 0) {
    target = Math.max(0, start - viewportHeight);
  } else if (index >= slideCount) {
    target = nextSectionTop > end ? nextSectionTop : end + viewportHeight;
  } else {
    const centre = index === 0
      ? caseStudiesDesktopRestRatio / 2
      : index === slideCount - 1 ? index - caseStudiesDesktopRestRatio / 2 : index;
    target = start + centre * step;
  }

  return {
    target,
    duration: clamp(caseStudiesDesktopTransitionDuration * Math.abs(target - scrollY) / step, 0.2, caseStudiesDesktopTransitionDuration),
    delay: 900,
  };
};

export const createCaseStudiesDesktopSnap = (section, getSceneTrigger, slideCount, gsapInstance, options = {}) => {
  const scrollKeys = new Set([' ', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End']);
  const heldKeys = new Set();
  let pointerDown = false;
  let pending = false;
  let direction = 0;
  let originIndex = null;
  let previousY = window.scrollY;
  let expectedY;
  let timeout;
  let tween;
  let destroyed = false;
  let moving = false;
  let automatic = false;
  const isBusy = () => pending || moving || pointerDown || heldKeys.size > 0;
  const notify = () => options.onStateChange?.();

  const clearTimer = () => {
    window.clearTimeout(timeout);
    timeout = undefined;
  };
  const stopTween = () => {
    tween?.kill();
    tween = undefined;
    moving = false;
    automatic = false;
    expectedY = undefined;
  };
  const cancel = () => {
    clearTimer();
    stopTween();
    pending = false;
    previousY = window.scrollY;
    notify();
  };
  const blocked = () => destroyed || document.hidden || section.contains(document.activeElement);
  const getTarget = () => {
    const trigger = getSceneTrigger();
    if (!trigger) return null;
    const nextSection = section.nextElementSibling;
    return getCaseStudiesDesktopSnapTarget({
      scrollY: window.scrollY,
      start: trigger.start,
      end: trigger.end,
      slideCount,
      direction,
      originIndex,
      viewportHeight: window.innerHeight,
      nextSectionTop: nextSection ? window.scrollY + nextSection.getBoundingClientRect().top : undefined,
    });
  };
  const moveTo = (target, onComplete) => {
    if (blocked() || pointerDown || heldKeys.size || !target) return false;
    clearTimer();
    stopTween();
    pending = false;
    moving = true;
    automatic = Boolean(target.automatic);
    if (automatic) {
      direction = 1;
      const trigger = getSceneTrigger();
      originIndex = trigger
        ? getCaseStudiesDesktopState((window.scrollY - trigger.start) / (trigger.end - trigger.start), slideCount).settledIndex
        : null;
    }
    const proxy = { value: window.scrollY };
    tween = gsapInstance.to(proxy, {
      value: target.target,
      duration: target.duration,
      ease: 'sine.inOut',
      onUpdate: () => {
        expectedY = proxy.value;
        window.scrollTo(0, proxy.value);
      },
      onComplete: () => {
        tween = undefined;
        moving = false;
        previousY = window.scrollY;
        onComplete?.();
        notify();
      },
    });
    notify();
    return true;
  };
  const settle = () => {
    timeout = undefined;
    if (!pending || pointerDown || heldKeys.size || blocked()) return;
    const target = getTarget();
    pending = false;
    if (!target || Math.abs(target.target - window.scrollY) <= 2) {
      notify();
      return;
    }
    moveTo(target);
  };
  const schedule = () => {
    clearTimer();
    if (!pending || pointerDown || heldKeys.size || blocked()) {
      notify();
      return;
    }
    const target = getTarget();
    timeout = window.setTimeout(settle, target?.delay ?? 900);
    notify();
  };
  const recordInput = (newDirection, fromY) => {
    if (!newDirection || blocked()) return;
    if (!pending || direction !== newDirection) {
      const trigger = getSceneTrigger();
      originIndex = trigger && fromY >= trigger.start && fromY <= trigger.end
        ? getCaseStudiesDesktopState((fromY - trigger.start) / (trigger.end - trigger.start), slideCount).settledIndex
        : null;
    }
    stopTween();
    direction = newDirection;
    pending = true;
    schedule();
  };
  const onWheel = (event) => {
    if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    recordInput(Math.sign(event.deltaY), window.scrollY);
  };
  const onKeyDown = (event) => {
    if (!scrollKeys.has(event.key) || event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
    heldKeys.add(event.key);
    const backwards = ['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey);
    recordInput(backwards ? -1 : 1, window.scrollY);
  };
  const onScroll = () => {
    const currentY = window.scrollY;
    if (expectedY !== undefined && Math.abs(currentY - expectedY) <= 2) {
      previousY = currentY;
      return;
    }
    if (currentY === previousY) return;
    const fromY = previousY;
    previousY = currentY;
    recordInput(Math.sign(currentY - fromY), fromY);
  };
  const onPointerDown = () => {
    pointerDown = true;
    if (tween) pending = true;
    clearTimer();
    stopTween();
    notify();
  };
  const release = (event) => {
    if (event.type === 'keyup') heldKeys.delete(event.key);
    else pointerDown = false;
    schedule();
  };
  const onBlur = () => {
    pointerDown = false;
    heldKeys.clear();
    cancel();
  };
  const onVisibility = () => {
    if (document.hidden) onBlur();
  };
  const listeners = [
    [window, 'wheel', onWheel],
    [window, 'scroll', onScroll],
    [window, 'keydown', onKeyDown],
    [window, 'keyup', release],
    [window, 'pointerdown', onPointerDown],
    [window, 'pointerup', release],
    [window, 'pointercancel', release],
    [window, 'blur', onBlur],
    [window, 'resize', cancel],
    [document, 'visibilitychange', onVisibility],
    [section, 'focusin', cancel],
  ];
  listeners.forEach(([target, event, listener]) => target.addEventListener(event, listener, { passive: true }));

  const destroy = () => {
    destroyed = true;
    cancel();
    listeners.forEach(([target, event, listener]) => target.removeEventListener(event, listener));
  };
  destroy.isBusy = isBusy;
  destroy.shouldCancel = () => moving || pending;
  destroy.isAutomatic = () => automatic;
  destroy.cancel = cancel;
  destroy.advanceTo = (target, onComplete) => !isBusy() && moveTo(target, onComplete);
  return destroy;
};

const createCaseStudiesDesktopScene = (section) => {
  const gsapInstance = getGsap();
  const scrollTrigger = getScrollTrigger();
  const pinEl = section.querySelector('.case-studies-sec__pin');
  const slides = Array.from(section.querySelectorAll('.case-studies-sec__slide'));
  const backgrounds = slides.map((slide) => slide.querySelector('.case-studies-sec__bg'));
  const galleryFrames = backgrounds.map((background) =>
    getCaseStudiesGalleryFrames(background, '.case-studies-sec__bg-frame')
  );
  const contents = slides.map((slide) => slide.querySelector('.case-studies-sec__content'));
  const progressLines = contents.map((content) =>
    getOrCreateCaseStudiesRail(content?.querySelector('.case-studies-sec__line'))
  );

  if (
    !pinEl ||
    !slides.length ||
    backgrounds.some((background) => !background) ||
    galleryFrames.some((frames) => !frames.length) ||
    contents.some((content) => !content) ||
    progressLines.some((line) => !line) ||
    !gsapInstance ||
    !scrollTrigger
  ) {
    return () => undefined;
  }

  const originalContentParents = contents.map((content) => ({ content, parent: content.parentElement }));
  const contentStage = getOrCreateContentStage(pinEl);
  const galleries = galleryFrames.map((frames, index) =>
    createCaseStudiesDesktopGallery(frames, progressLines[index], gsapInstance, { onComplete: () => syncPlayback() })
  );
  let sceneTimeline;
  let observer;
  let visible = false;
  let focused = section.contains(document.activeElement);
  let activeIndex = 0;
  let lastSettledIndex = 0;
  const previewed = new Set();
  let destroySnap;
  let preparingAdvance = false;
  let advanceGeneration = 0;
  let destroyed = false;

  const syncPlayback = () => {
    if (destroyed) return;
    const allowed = visible && !document.hidden && !focused;
    if ((!visible && destroySnap?.isAutomatic() || document.hidden || focused) && destroySnap?.shouldCancel()) destroySnap.cancel();
    const ready = allowed && !destroySnap?.isBusy() && !sceneTimeline?.scrollTrigger?.getTween()?.isActive();
    galleries.forEach((gallery, index) => {
      if (index === activeIndex && ready) gallery.play();
      else gallery.pause();
    });
    if (!ready || activeIndex === null || !destroySnap || preparingAdvance || activeIndex === galleries.length - 1 || !galleries[activeIndex].isComplete()) return;
    const origin = activeIndex;
    const generation = advanceGeneration;
    const next = origin + 1;
    preparingAdvance = true;
    const preparation = galleries[next].prepare();
    preparation.then((available) => {
      if (generation !== advanceGeneration) return;
      preparingAdvance = false;
      if (!available || destroyed || activeIndex !== origin || !galleries[origin].isComplete() || !visible || document.hidden || focused || destroySnap.isBusy()) return;
      const trigger = sceneTimeline?.scrollTrigger;
      const step = trigger ? (trigger.end - trigger.start) / (slides.length - 1) : 0;
      const target = trigger.start + (next === slides.length - 1 ? next - caseStudiesDesktopRestRatio / 2 : next) * step;
      destroySnap.advanceTo({ target, duration: caseStudiesDesktopTransitionDuration, automatic: true });
    });
  };

  const syncScene = () => {
    const state = getCaseStudiesDesktopState(sceneTimeline?.progress() ?? 0, slides.length);
    new Set([state.from, state.to]).forEach((index) => {
      if (index !== lastSettledIndex && !previewed.has(index)) {
        galleries[index].reset();
        previewed.add(index);
      }
    });
    if (state.settledIndex !== null && state.settledIndex !== lastSettledIndex) {
      lastSettledIndex = state.settledIndex;
      advanceGeneration += 1;
      preparingAdvance = false;
      previewed.clear();
      galleries[lastSettledIndex + 1]?.prepare();
    }
    activeIndex = state.settledIndex;
    syncPlayback();
  };

  section.classList.add('is-enhanced');
  contents.forEach((content) => contentStage.appendChild(content));
  gsapInstance.registerPlugin(scrollTrigger);
  gsapInstance.set(backgrounds, { yPercent: 100, zIndex: 1 });
  gsapInstance.set(backgrounds[0], { yPercent: 0, zIndex: 2 });
  gsapInstance.set(contents, { autoAlpha: 0, y: 0, zIndex: 1 });
  gsapInstance.set(contents[0], { autoAlpha: 1, zIndex: 2 });

  if (slides.length > 1) {
    sceneTimeline = gsapInstance.timeline({
      onUpdate: syncScene,
      scrollTrigger: {
        trigger: section,
        pin: pinEl,
        anticipatePin: 1,
        scrub: 0.85,
        onScrubComplete: syncPlayback,
        start: 'top top',
        end: () => `+=${getPinHeight(pinEl) * (slides.length - 1)}`,
        invalidateOnRefresh: true,
      },
    });

    slides.slice(0, -1).forEach((slide, index) => {
      const previousContent = contents[index];
      const nextContent = contents[index + 1];
      const nextBackground = backgrounds[index + 1];
      const transitionDuration = caseStudiesDesktopTransitionDuration * (1 - 2 * caseStudiesDesktopRestRatio);
      const restDuration = caseStudiesDesktopTransitionDuration * caseStudiesDesktopRestRatio;
      sceneTimeline
        .to({}, { duration: restDuration })
        .to(previousContent, {
          autoAlpha: 0,
          duration: transitionDuration * 0.3,
          ease: 'sine.inOut',
        })
        .set(previousContent, { zIndex: 1 })
        .to(nextBackground, {
          yPercent: 0,
          duration: transitionDuration * 0.45,
          ease: 'sine.inOut',
        })
        .set(nextContent, { autoAlpha: 0, y: 0, zIndex: 2 })
        .to(nextContent, {
          autoAlpha: 1,
          y: 0,
          duration: transitionDuration * 0.25,
          ease: 'sine.out',
        })
        .to({}, { duration: restDuration });
    });
    syncScene();
  }

  destroySnap = createCaseStudiesDesktopSnap(section, () => sceneTimeline?.scrollTrigger, slides.length, gsapInstance, { onStateChange: syncPlayback });

  const pauseForFocus = () => {
    focused = true;
    syncPlayback();
  };
  const resumeAfterFocusLeaves = () => {
    queueMicrotask(() => {
      focused = section.contains(document.activeElement);
      syncPlayback();
    });
  };

  if (typeof IntersectionObserver === 'undefined') {
    visible = true;
  } else {
    observer = new IntersectionObserver(([entry]) => {
      visible = entry.intersectionRatio >= 0.6;
      if (visible) {
        preloadCaseStudiesMedia(section, '[data-case-studies-media]');
        galleries[(activeIndex ?? lastSettledIndex) + 1]?.prepare();
      }
      syncPlayback();
    }, { threshold: 0.6 });
    observer.observe(pinEl);
  }
  document.addEventListener('visibilitychange', syncPlayback);
  section.addEventListener('focusin', pauseForFocus);
  section.addEventListener('focusout', resumeAfterFocusLeaves);
  syncPlayback();

  return () => {
    if (destroyed) return;
    destroyed = true;
    observer?.disconnect();
    destroySnap();
    galleries.forEach((gallery) => gallery.destroy());
    sceneTimeline?.scrollTrigger?.kill();
    sceneTimeline?.kill();
    document.removeEventListener('visibilitychange', syncPlayback);
    section.removeEventListener('focusin', pauseForFocus);
    section.removeEventListener('focusout', resumeAfterFocusLeaves);
    gsapInstance.set([...backgrounds, ...galleryFrames.flat(), ...contents, ...progressLines], {
      clearProps: 'transform,clip-path,visibility,opacity,zIndex,--case-studies-progress',
    });
    originalContentParents.forEach(({ content, parent }) => parent?.appendChild(content));
    contentStage.remove();
    section.classList.remove('is-enhanced');
  };
};

export function initCaseStudies(scope = document) {
  if (!scope?.querySelectorAll || typeof window === 'undefined') {
    return;
  }

  getCaseStudySections(scope).forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const desktopMedia = window.matchMedia(desktopAutoplayMediaQuery);
    const snapMedia = window.matchMedia(caseStudiesSnapMediaQuery);
    const mobileMedia = window.matchMedia(mobileCaseStudiesMediaQuery);
    const reducedMotionMedia = window.matchMedia(reducedMotionMediaQuery);
    let destroyAutoplay = () => undefined;
    let destroyScrollSnap = () => undefined;
    let destroyMobileSlider = () => undefined;
    let destroyMediaPreload = () => undefined;

    const syncCaseStudiesMode = () => {
      destroyAutoplay();
      destroyScrollSnap();
      destroyMobileSlider();
      destroyMediaPreload();
      destroyAutoplay = canUseCaseStudiesDesktopScene()
        ? createCaseStudiesDesktopScene(section)
        : () => undefined;
      destroyScrollSnap = canUseCaseStudiesSnap()
        ? createCaseStudiesScrollSnap(section)
        : () => undefined;
      destroyMobileSlider = mobileMedia.matches
        ? createMobileCaseStudiesSlider(section)
        : () => undefined;
      destroyMediaPreload = initCaseStudiesMediaPreload(
        section,
        mobileMedia.matches ? '[data-case-studies-mobile-media]' : '[data-case-studies-media]'
      );
    };

    desktopMedia.addEventListener('change', syncCaseStudiesMode);
    snapMedia.addEventListener('change', syncCaseStudiesMode);
    mobileMedia.addEventListener('change', syncCaseStudiesMode);
    reducedMotionMedia.addEventListener('change', syncCaseStudiesMode);
    initializedSections.set(section, () => {
      destroyAutoplay();
      destroyScrollSnap();
      destroyMobileSlider();
      destroyMediaPreload();
      desktopMedia.removeEventListener('change', syncCaseStudiesMode);
      snapMedia.removeEventListener('change', syncCaseStudiesMode);
      mobileMedia.removeEventListener('change', syncCaseStudiesMode);
      reducedMotionMedia.removeEventListener('change', syncCaseStudiesMode);
    });

    syncCaseStudiesMode();
  });
}

globalThis.initCaseStudies = initCaseStudies;

if (typeof document !== 'undefined' && !document.querySelector('.bold-loader-new')) {
  initCaseStudies();
}
